import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { FaArrowLeft } from 'react-icons/fa';
import { AudioContext } from '../context/AudioContext';
import TrackItem from './TrackItem';
import '../styles/OtherUserAllTracks.css';

const OtherUserAllTracks = () => {
  const { id: userId } = useParams();
  const navigate = useNavigate();
  const { handleTrackClick } = useContext(AudioContext);
  const [allTracks, setAllTracks] = useState([]);
  const [pinnedTracks, setPinnedTracks] = useState([]);
  const [userData, setUserData] = useState({
    artistName: '',
    profilePicture: '',
    bio: '',
  });
  const [loading, setLoading] = useState(true);
  const auth = getAuth();
  const currentUserUid = auth.currentUser?.uid;

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) {
        console.log('No userId provided to OtherUserAllTracks');
        setLoading(false);
        return;
      }

      try {
        const userResponse = await axios.get(
          `https://sync-api-troz.onrender.com/api/users/firebase/${userId}`
        );
        setUserData(userResponse.data);

        if (currentUserUid) {
          const pinnedResponse = await axios.get(
            `https://sync-api-troz.onrender.com/api/uploads/pinned`,
            { params: { firebaseUid: currentUserUid } }
          );
          setPinnedTracks(pinnedResponse.data);
        }

        const uploadsResponse = await axios.get(
          `https://sync-api-troz.onrender.com/api/uploads`,
          { params: { firebaseUid: userId } }
        );

        const sortedTracks = uploadsResponse.data.sort(
          (a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt)
        );
        setAllTracks(sortedTracks);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching other user all tracks:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, currentUserUid]);

  const handleLikeTrack = async (trackId, trackUserId) => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      window.alert('You must be logged in to like a track.');
      return;
    }

    if (trackUserId === currentUser.uid) {
      window.alert("You can't like your own track.");
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      await axios.post(
        'https://sync-api-troz.onrender.com/api/uploads/like',
        { trackId, firebaseUid: currentUser.uid },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.alert('Track liked successfully!');
      setAllTracks((prev) =>
        prev.map((track) =>
          track._id === trackId
            ? { ...track, likes: (track.likes || 0) + 1, likedBy: [...(track.likedBy || []), currentUser.uid] }
            : track
        )
      );
    } catch (error) {
      console.error('Error liking track:', error.response?.data || error.message);
      window.alert(
        error.response?.data?.message === 'You have already liked this track'
          ? 'You have already liked this track.'
          : 'An error occurred while liking the track.'
      );
    }
  };

  const handleStarTrack = async (trackId) => {
    const firebaseUid = auth.currentUser?.uid;
    if (!firebaseUid) {
      window.alert('You must be logged in to star a track.');
      return;
    }

    try {
      await axios.post('https://sync-api-troz.onrender.com/api/uploads/star', {
        trackId,
        userId: firebaseUid,
      });
      window.alert('Track starred successfully!');
      setAllTracks((prev) =>
        prev.map((track) =>
          track._id === trackId
            ? {
                ...track,
                stars: (track.stars || 0) + 1,
                starredBy: [...(track.starredBy || []), firebaseUid],
              }
            : track
        )
      );
    } catch (error) {
      console.error('Error starring track:', error.response?.data || error.message);
      window.alert(
        error.response?.data?.message === 'You have already starred a track from this artist'
          ? 'You already starred a track from this user.'
          : 'An error occurred while starring the track.'
      );
    }
  };

  if (loading) {
    return (
      <div className="other-user-all-tracks-loading">
        <div className="loader"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="other-user-all-tracks-page">
      <div className="other-user-all-tracks-container">
        <div className="other-user-all-tracks-banner">
          {userData.profilePicture ? (
            <img
              src={userData.profilePicture}
              alt="Profile"
              className="other-user-all-tracks-banner-image"
            />
          ) : (
            <div className="other-user-all-tracks-banner-placeholder" />
          )}
          <p className="other-user-all-tracks-artist-name">{userData.artistName}</p>
        </div>

        <div className="other-user-all-tracks-header">
          <button onClick={() => navigate(-1)} className="other-user-all-tracks-back-button">
            <FaArrowLeft size={24} />
          </button>
          <p className="other-user-all-tracks-header-text">All Tracks</p>
          <div className="other-user-all-tracks-placeholder" />
        </div>

        <div className="other-user-all-tracks-results">
          {allTracks.length > 0 ? (
            allTracks.map((track) => (
              <TrackItem
                key={track._id}
                track={track}
                onPlay={() => handleTrackClick(track)}
                onLike={() => handleLikeTrack(track._id, track.user?.firebaseUid)}
                onStar={() => handleStarTrack(track._id)}
                currentUserId={currentUserUid}
                isOwner={false}
                showPinButton={false}
                showDeleteButton={false}
              />
            ))
          ) : (
            <p className="other-user-all-tracks-no-results">This user has no tracks yet.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default OtherUserAllTracks;